import Vue from 'vue'
import * as types from '../mutation-types'

import posts from '@/api/posts'
import users from '@/api/users'

const state = {
  posts: {},
  newPostIds: [],
  likes: {},
  projectPosts: {},
  postType: '',
  creatingComment: false,
  userPosts: {},
  sortedUserPosts: {}
}

const getters = {
  postType: (state) => {
    return state.postType
  },
  likedPost: (state) => (id, username) => {
    return state.likes[id] ? state.likes[id][username] : false
  },
  newPostIds: (state) => {
    return state.newPostIds
  },
  projectPosts: (state) => (id, descending) => {
    if (!state.projectPosts || !state.projectPosts[id]) {
      return []
    }

    let ids = state.projectPosts[id]
    if (ids.length > 1 && descending) {
      ids = ids.slice(0).reverse()
    }

    // ensure that recommended project is always the last post
    // if (ids.length > 1 && !descending && ids[0] === 1) {
    //   ids = ids.slice(0)
    //   ids.shift()
    //   ids.push(1)
    // }

    return ids.map((postID) => { return state.posts[postID] || null }).filter((post) => { return !!post })
  },
  getPostType: (state) => {
    return state.postType
  },
  userPosts: (state) => (descending) => {
    const id = 'userPosts'
    if (!state.userPosts[id]) {
      return []
    }

    let ids = state.userPosts[id]
    if (ids.length > 1 && descending) {
      ids = ids.slice(0).reverse()
    }

    return ids.map((postID) => { return state.posts[postID] || null }).filter((post) => { return !!post })
  },
  sortedUserPosts: (state) => (descending) => {
    const id = 'userPosts'
    if (!state.sortedUserPosts[id]) {
      return []
    }
    let ids = state.sortedUserPosts[id]
    if (ids.length > 1 && descending) {
      ids = ids.slice(0).reverse()
    }

    return ids.map((postID) => { return state.posts[postID] || null }).filter((post) => { return !!post })
  },
  attachmentsByPost: (state) => (postID) => {
    return state.posts[postID.attachments]
  },
  getPost: (state) => (postID) => {
    return state.posts[postID]
  }
}

const actions = {
  setPostType ({ commit }, postType) {
    commit(types.SET_POST_TYPE, postType)
  },
  getPost ({ commit, state }, { id, force }) {
    return new Promise((resolve, reject) => {
      if (state.posts[id] && !force) {
        if (!state.posts[id].isPrivate) {
          return resolve(state.posts[id])
        }
      }
      posts.get(id).then((response) => {
        const responsePost = response.data.result.post
        responsePost.saved = true
        commit(types.ADD_POST, { responsePost, saved: true })
        resolve(responsePost)
      }, (error) => {
        commit(types.DELETE_POST, { id })
        reject(error)
      })
    })
  },
  getProjectPosts ({ commit }, { id, limit, offset, sort, page }) {
    if (page === 1) {
      commit(types.CLEAR_PROJECT_POSTS, id)
    }

    let before = 0
    let since = 0

    if (state.projectPosts[id] && state.projectPosts[id].length > 0) {
      if (sort === 'newest') {
        const newestPost = state.projectPosts[id][0] || 0
        before = state.posts[newestPost].priority
      } else {
        const oldestPost = state.projectPosts[id][state.projectPosts[id].length - 1] || 0
        since = state.posts[oldestPost].priority
      }
    }

    return new Promise((resolve, reject) => {
      posts.getProjectPosts(id, limit, offset, sort, before, since).then((response) => {
        const posts = response.data.result.posts
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const paywall = response.data.result.paywall
        posts.forEach((post) => {
          commit(types.ADD_POST, { post, saved: false })
          commit(types.ADD_PROJECT_POST, post)
        })
        resolve({ posts, total, hasMore, paywall })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectPostsAndComments ({ commit }, { id, limit, sort, since }) {
    return new Promise((resolve, reject) => {
      posts.getProjectPostsAndComments(id, limit, sort, since).then((response) => {
        const contributions = response.data.result.contributions
        const total = response.data.result.total || 0
        resolve({ contributions, total })
      }, (error) => {
        reject(error)
      })
    })
  },
  loadToPost ({ commit }, { id, postID, sort, limit }) {
    return new Promise((resolve, reject) => {
      posts.loadToPost(id, postID, sort, limit).then((response) => {
        const posts = response.data.result.posts
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        posts.forEach((post) => {
          commit(types.ADD_POST, { post })
          commit(types.ADD_PROJECT_POST, post)
        })
        resolve({ posts, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  loadToPostsAndComments ({ commit }, { id, postID, sort }) {
    return new Promise((resolve, reject) => {
      posts.loadToPostsAndComments(id, postID, sort).then((response) => {
        const contributions = response.data.result.contributions
        const total = response.data.result.total || 0
        resolve({ contributions, total })
      }, (error) => {
        reject(error)
      })
    })
  },
  createPost ({ commit }, data) {
    return new Promise((resolve, reject) => {
      posts.create(data).then((response) => {
        const responsePost = response.data.result.post
        commit(types.ADD_POST, { responsePost, saved: true })
        commit(types.ADD_PROJECT_POST, responsePost)

        commit(types.UPDATE_PROJECT_POST_COUNT, { id: responsePost.projectID, count: 1 })

        if (response.data.result.autoSubscribed) {
          commit(types.SUBSCRIBE_PROJECT, { id: responsePost.projectID })
          commit(types.SUBSCRIBED_TO_PROJECT, { id: responsePost.projectID, username: responsePost.user.userName, subscribed: true })
        }

        resolve(responsePost)
      }, (error) => {
        reject(error)
      })
    })
  },

  updatePost ({ commit }, post) {
    return new Promise((resolve, reject) => {
      posts.update(post.id, post).then((response) => {
        const responsePost = response.data.result.post
        commit(types.ADD_POST, { responsePost, saved: true })
        resolve(responsePost)
      }, (error) => {
        reject(error)
      })
    })
  },

  updatePostBody ({ commit }, post) {
    return new Promise((resolve, reject) => {
      posts.update(post.id, post).then((response) => {
        commit(types.UPDATE_POST_BODY, { id: post.id, body: response.data.result.post.body })
        resolve(state.posts[post.id])
      }, (error) => {
        reject(error)
      })
    })
  },

  updatePostPriority ({ commit }, post) {
    return new Promise((resolve, reject) => {
      posts.updatePriority(post.id, post).then((response) => {
        commit(types.UPDATE_POST_PRIORITY, { id: post.id, body: response.data.result.post.priority })
        resolve(state.posts[post.id])
      }, (error) => {
        reject(error)
      })
    })
  },
  likePost ({ commit }, { post, username }) {
    return new Promise((resolve, reject) => {
      posts.like(post.id).then((response) => {
        commit(types.LIKE_POST, { id: post.id })
        commit(types.LIKED_POST, { id: post.id, username, liked: true })
        if (response.data.result.liked) {
          commit(types.LIKE_PROJECT, { id: post.projectID })
          commit(types.LIKED_PROJECT, { id: post.projectID, username, liked: true })
        }
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  unlikePost ({ commit }, { post, username }) {
    return new Promise((resolve, reject) => {
      posts.unlike(post.id).then(() => {
        commit(types.UNLIKE_POST, { id: post.id })
        commit(types.LIKED_POST, { id: post.id, username, liked: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  setEdited ({ commit }, { id, edited }) {
    commit(types.SET_EDITED, { id, edited })
  },
  likedPost ({ commit, state }, { id, username }) {
    return new Promise((resolve, reject) => {
      if (state.likes[id] && state.likes[id][username] !== undefined) {
        return resolve(state.likes[id][username])
      }

      if (state.likes.cached && state.likes.cached[username]) {
        return resolve(false)
      }

      posts.hasLiked(id, username).then((response) => {
        const liked = response.data.result.liked
        commit(types.LIKED_POST, { id, username, liked })
        resolve(liked)
      }, (error) => {
        reject(error)
      })
    })
  },
  postLikedBy ({ commit }, { id, limit, offset }) {
    return new Promise((resolve, reject) => {
      posts.getLikes(id, limit, offset).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  cacheLikedPosts ({ commit }, username) {
    if (state.likes.cached && state.likes.cached[username]) {
      return
    }

    users.getLikedPostIDs(username).then((response) => {
      commit(types.LIKED_POSTS_CACHED, { username })
      response.data.result.posts.forEach((id) => {
        commit(types.LIKED_POST, { id, username, liked: true })
      })
    })
  },
  deletePost ({ commit }, post) {
    return new Promise((resolve, reject) => {
      posts.delete(post.id).then(() => {
        commit(types.DELETE_POST, post)
        commit(types.UPDATE_PROJECT_POST_COUNT, { id: post.projectID, count: -1 })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  addPostAttachments ({ commit }, { id, attachments }) {
    return new Promise((resolve, reject) => {
      posts.addAttachment(id, { attachments }).then((response) => {
        const newAttachments = response.data.result.attachments
        newAttachments.forEach((attachment) => {
          commit(types.ADD_POST_ATTACHMENT, attachment)
        })
        resolve(newAttachments)
      }, (error) => {
        reject(error)
      })
    })
  },
  updatePostAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      posts.updateAttachment(id, attachment.id, attachment).then((response) => {
        const updatedAttachment = response.data.result.attachment
        commit(types.UPDATE_POST_ATTACHMENT, updatedAttachment)
        resolve(updatedAttachment)
      }, (error) => {
        reject(error)
      })
    })
  },
  deletePostAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      posts.deleteAttachment(id, attachment.id).then((response) => {
        commit(types.DELETE_POST_ATTACHMENT, { id, attachmentID: attachment.id })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  copyPostAttachment ({ commit }, { id, attachment }) {
    return new Promise((resolve, reject) => {
      posts.copyAttachment(id, attachment.id).then((response) => {
        resolve(response.data.result.storageKey)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateCreatingComment ({ commit }) {
    commit(types.UPDATE_CREATING_COMMENT)
  },
  getAllForUser ({ commit }, { limit, offset, sort, page }) {
    if (page === 1) {
      commit(types.CLEAR_USER_POSTS, 'userPosts')
    }

    let before = 0
    const since = 0
    const id = 'userPosts'

    if (state.userPosts[id] && state.userPosts[id].length > 0) {
      const newestPost = state.userPosts[id][0] || 0
      before = state.posts[newestPost].id
    }

    return new Promise((resolve, reject) => {
      posts.getAllForUser(limit, offset, sort, before, since).then((response) => {
        const posts = response.data.result.posts
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        posts.forEach((post) => {
          commit(types.ADD_POST, { post, saved: true })
          if (post.project !== undefined) {
            commit(types.ADD_PROJECT, post.project)
          }
          commit(types.ADD_USER_POST, post)
        })
        resolve({ posts, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  sortAttachmentsForPost ({ commit }, postID) {
    commit(types.SORT_ATTACHMENTS_FOR_POST, postID)
  },
  resetForFeed ({ commit }) {
    commit(types.RESET_ON_FEED)
  },
  socketMessage ({ dispatch, state, commit }, message) {
    const payload = message.payload || {}

    const postMessage = payload.type === 'post'

    const projectChildPostMessage = payload.type === 'project' && payload.childType === 'post'
    const projectSubPostMessage = payload.type === 'project' && payload.subType === 'post'

    if (!postMessage && !projectChildPostMessage && !projectSubPostMessage) {
      return
    }

    // new section added
    if (projectChildPostMessage && payload.action === 'add') {
      dispatch('getProjectPosts', { id: payload.id, limit: 1, socket: true })
    }

    // make sure post is in the store
    if ((postMessage && !state.posts[payload.id]) || (projectSubPostMessage && !state.posts[payload.subTypeID]) || (projectChildPostMessage && !state.posts[payload.childTypeID])) {
      return
    }

    // post updated
    if (projectChildPostMessage && payload.action === 'update') {
      dispatch('getPost', { id: payload.childTypeID, force: true }).catch(() => {})
    }

    // post deleted
    if (projectChildPostMessage && payload.action === 'delete') {
      commit(types.DELETE_POST, state.posts[payload.childTypeID])
    }

    // new comment added
    if (projectSubPostMessage && payload.action === 'add' && payload.childType === 'comment') {
      dispatch('getPostComments', { id: payload.subTypeID, limit: 10, socket: true })
    }

    // comment updated
    if (projectSubPostMessage && payload.action === 'update' && payload.childType === 'comment') {
      dispatch('getComment', { id: payload.childTypeID, force: true }).catch(() => {})
    }

    // comment deleted
    if (projectSubPostMessage && payload.action === 'delete' && payload.childType === 'comment') {
      dispatch('deleteLocalComment', payload.childTypeID)
    }
  },
  postUpdateSettings ({ commit }, { id, data }) {
    return new Promise((resolve, reject) => {
      posts.updateSettings(id, data).then((response) => {
        commit(types.UPDATE_POST_SETTINGS, { id, data })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  }
}

const mutations = {
  [types.SET_POST_TYPE] (state, postType) {
    state.postType = postType
  },

  [types.CLEAR_PROJECT_POSTS] (state, projectID) {
    Vue.set(state.projectPosts, projectID, [])
  },

  [types.CLEAR_USER_POSTS] (state, projectID) {
    Vue.set(state.projectPosts, 'userPosts', [])
  },
  [types.SORT_ATTACHMENTS_FOR_POST] (state, postID) {
    const localAttachments = state.posts[postID].attachments
    localAttachments.sort(function (a, b) { return a.priority - b.priority })

    state.posts[postID].attachments = localAttachments
  },
  [types.ADD_POST] (state, params) {
    if (params.responsePost) {
      if (params.saved) {
        params.responsePost.saved = true
      }
      params.responsePost.edited = false
      Vue.set(state.posts, params.responsePost.id, params.responsePost)
    } else {
      if (params.saved) {
        params.post.saved = true
      }
      params.post.edited = false
      Vue.set(state.posts, params.post.id, params.post)
    }
  },

  [types.UPDATE_POST_BODY] (state, params) {
    Vue.set(state.posts[params.id], 'body', params.body)
  },

  [types.UPDATE_POST_PRIORITY] (state, params) {
    Vue.set(state.posts[params.id], 'priority', params.priority)
  },

  [types.ADD_PROJECT_POST] (state, post) {
    if (!state.projectPosts[post.projectID]) {
      Vue.set(state.projectPosts, post.projectID, [])
    }

    if (state.projectPosts[post.projectID].indexOf(post.id) >= 0) {
      return
    }

    let firstID = 0
    if (state.projectPosts[post.projectID].length > 0) {
      const lowestPriority = state.projectPosts[post.projectID][0] || 0
      firstID = state.posts[lowestPriority].priority
    }

    if (post.priority < firstID) {
      state.projectPosts[post.projectID].unshift(post.id)
    } else {
      state.projectPosts[post.projectID].push(post.id)
    }
  },

  [types.ADD_USER_POST] (state, post) {
    if (!state.userPosts.userPosts || !state.sortedUserPosts.userPosts) {
      Vue.set(state.userPosts, 'userPosts', [])
      Vue.set(state.sortedUserPosts, 'userPosts', [])
    }

    if (state.userPosts.userPosts.indexOf(post.id) >= 0 || state.sortedUserPosts.userPosts.indexOf(post.id) >= 0) {
      return
    }

    let firstID = 0
    if (state.userPosts.userPosts.length > 0) {
      const lowestPriority = state.userPosts.userPosts[0] || 0
      firstID = state.posts[lowestPriority].id
    }
    if (post.priority < firstID) {
      state.userPosts.userPosts.unshift(post.id)
    } else {
      state.userPosts.userPosts.push(post.id)
    }

    state.sortedUserPosts.userPosts.push(post.id)
    state.sortedUserPosts.userPosts.sort()
  },

  [types.LIKE_POST] (state, params) {
    state.posts[params.id].metadata.likes++
  },

  [types.UNLIKE_POST] (state, params) {
    if (state.posts[params.id].metadata.likes > 0) {
      state.posts[params.id].metadata.likes--
    }
  },

  [types.LIKED_POSTS_CACHED] (state, params) {
    if (!state.likes.cached) {
      state.likes.cached = {}
    }

    state.likes.cached[params.username] = true
  },

  [types.SET_EDITED] (state, params) {
    if (state.posts[params.id]) {
      state.posts[params.id].edited = params.edited
    }
  },

  [types.LIKED_POST] (state, params) {
    if (!state.likes[params.id]) {
      Vue.set(state.likes, params.id, {})
    }

    Vue.set(state.likes[params.id], params.username, params.liked)
  },

  [types.UPDATE_POST_COMMENT_COUNT] (state, params) {
    if (!state.posts[params.id]) {
      return
    }

    state.posts[params.id].metadata.comments += params.count

    if (state.posts[params.id].metadata.comments < 0) {
      state.posts[params.id].metadata.comments = 0
    }
  },
  [types.DELETE_POST] (state, post) {
    delete state.posts[post.id]

    if (state.projectPosts[post.projectID]) {
      Vue.set(state.projectPosts, post.projectID, state.projectPosts[post.projectID].filter((id) => id !== post.id))
    }
    if (state.userPosts.userPosts) {
      Vue.set(state.userPosts, 'userPosts', state.userPosts.userPosts.filter((id) => id !== post.id))
    }
  },
  [types.DELETE_POST_ATTACHMENT] (state, params) {
    if (state.posts[params.id].metadata.attachment > 0) {
      state.posts[params.id].metadata.attachment--
    }

    Vue.set(state.posts[params.id], 'attachments', state.posts[params.id].attachments.filter((attachment) => attachment.id !== params.attachmentID))
  },
  [types.ADD_POST_ATTACHMENT] (state, attachment) {
    if (!state.posts[attachment.postID]) {
      return
    }

    if (!state.posts[attachment.postID].attachments) {
      Vue.set(state.posts[attachment.postID], 'attachments', [])
    }

    state.posts[attachment.postID].attachments.push(attachment)
  },
  [types.UPDATE_POST_ATTACHMENT] (state, attachment) {
    if (!state.posts[attachment.postID]) {
      return
    }

    let index = -1
    state.posts[attachment.postID].attachments.forEach((a, offset) => {
      if (a.id === attachment.id) {
        index = offset
      }
    })

    Vue.set(state.posts[attachment.postID].attachments, index, attachment)
  },
  [types.UPDATE_CREATING_COMMENT] (state) {
    state.creatingComment = true
  },
  [types.RESET_ON_LOGOUT] (state) {
    if (!state.creatingComment) {
      state.posts = {}
      state.projectPosts = {}
    }
    state.newPostIds = []
    state.likes = {}
    state.postType = ''
    state.creatingComment = false
  },
  [types.RESET_ON_FEED] (state) {
    state.posts = {}
    state.userPosts = {}
    state.sortedUserPosts = {}
  },
  [types.ADD_NEW_POST_ID] (state, params) {
    state.newPostIds.push(params.id)
  },
  [types.UPDATE_POST_SETTINGS] (state, params) {
    const id = params.id
    state.posts[id].maxAttachments = params.data.attachmentLimit
    state.posts[id].maxWords = params.data.wordLimit
    state.posts[id].postFeatures.canDelete = params.data.canDelete
    state.posts[id].postFeatures.canEdit = params.data.canEdit
    state.posts[id].postFeatures.name = params.data.name
    const attachments = state.posts[id].attachments
    const attachmentsLocked = params.data.attachmentsLocked

    attachments.forEach((attachment) => {
      attachment.locked = false
      if (attachmentsLocked && attachmentsLocked.length > 0) {
        attachmentsLocked.forEach((attachmentLockedId) => {
          if (attachment.id === attachmentLockedId) {
            attachment.locked = true
          }
        })
      }
    })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
